/**
 * What a typed website becomes before Saidiseire saves it - the add dialog's
 * live preview.
 *
 * A MIRROR of the API's `crm-monitoring/lib/url.ts`, which is the one that
 * decides: the route normalises again and answers `bad_url` for anything it
 * refuses. This copy exists so the operator sees the address that will actually
 * be checked while typing it, and sees why a paste will be refused before
 * pressing Add rather than after.
 *
 * The rules, in the order they apply:
 *  - trim; no scheme means https (the scheme is only recognised with "://", so
 *    "firma.ee:8080" is a host with a port, not a scheme called "firma.ee");
 *  - http or https only;
 *  - user info, query and hash are dropped, the path is kept;
 *  - the host comes from `new URL()`, which lower-cases it and turns an
 *    international name into punycode - the form the checker connects to;
 *  - one trailing dot is dropped ("firma.ee." and "firma.ee" are one site);
 *  - IP literals are refused, v4 and v6. WHATWG parsing turns every IPv4
 *    shorthand (2130706433, 0x7f.1, 127.1) into dotted form first, and keeps the
 *    brackets on IPv6, so the two tests below see every spelling;
 *  - a host without a dot is refused (localhost, an intranet name), and so is a
 *    special-use or private-network ending (`*.localhost`, `.local`,
 *    `.internal`, `.home.arpa`, `.lan` ...), the API's `LOCAL_SUFFIXES`;
 *  - an explicit non-default port is refused: the certificate look always
 *    connects to 443, so a :8443 site would be judged by another certificate;
 *  - the stored address is at most 500 characters.
 *
 * One rule is NOT mirrored: the server also needs the host to have a
 * registrable domain under the public suffix list (`parseDomain`), and that
 * list is too big to ship to a browser. A name with a made-up ending passes
 * here and is refused as `bad_url` on Add.
 */

export type MonitorUrlProblem =
  | "empty"
  | "badUrl"
  | "badProtocol"
  | "ipLiteral"
  | "noDot"
  | "localName"
  | "port"
  | "tooLong";

/** Mirrors the API's `MAX_INPUT_CHARS` and `MAX_URL_CHARS`: the longest text
 *  read, and the longest address stored. */
export const MONITOR_URL_INPUT_MAX = 2_000;
export const MONITOR_URL_MAX = 500;

export type NormalizedMonitorUrl =
  | { ok: true; url: string; host: string }
  | { ok: false; problem: MonitorUrlProblem };

const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:\/\//i;
const DOTTED_V4 = /^\d{1,3}(?:\.\d{1,3}){3}$/;

/** Mirrors the API's `LOCAL_SUFFIXES`: endings that never name a public website. */
export const MONITOR_LOCAL_SUFFIXES: readonly string[] = [
  "localhost",
  "local",
  "localdomain",
  "internal",
  "intranet",
  "lan",
  "home",
  "corp",
  "arpa",
  "test",
  "invalid",
  "example",
  "onion",
];

export function normalizeMonitorUrl(raw: string): NormalizedMonitorUrl {
  const text = raw.trim();
  if (!text) return { ok: false, problem: "empty" };
  if (text.length > MONITOR_URL_INPUT_MAX) return { ok: false, problem: "tooLong" };

  let parsed: URL;
  try {
    parsed = new URL(HAS_SCHEME.test(text) ? text : `https://${text}`);
  } catch {
    return { ok: false, problem: "badUrl" };
  }
  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return { ok: false, problem: "badProtocol" };

  let host = parsed.hostname.toLowerCase();
  if (host.endsWith(".")) host = host.slice(0, -1);
  if (!host) return { ok: false, problem: "badUrl" };
  if (host.startsWith("[") || DOTTED_V4.test(host)) return { ok: false, problem: "ipLiteral" };
  if (!host.includes(".")) return { ok: false, problem: "noDot" };
  if (MONITOR_LOCAL_SUFFIXES.some((suffix) => host === suffix || host.endsWith(`.${suffix}`))) {
    return { ok: false, problem: "localName" };
  }
  // `URL` already empties `port` when it is the scheme's default, so anything
  // left here is a port somebody typed on purpose.
  if (parsed.port !== "") return { ok: false, problem: "port" };

  const url = `${parsed.protocol}//${host}${parsed.pathname || "/"}`;
  if (url.length > MONITOR_URL_MAX) return { ok: false, problem: "tooLong" };
  return { ok: true, url, host };
}
